import { createCurrencyFormatter } from "@/common/helpers/format-currency";
import { buildDateString, buildTimeString } from "@/common/helpers/format-date";
import { getCurrentNumberFormatLocale } from "@/common/helpers/number-format";
import { EmptyValue } from "@/components/ui/empty-value";
import { userConfigStore } from "@/store/user-config-store";
import { ReactNode } from "react";

const NUMERIC_PATTERN = /^[0-9]+(?:\.[0-9]+)?$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z?)?$/;
const BOOLEAN_STRINGS = new Set(["true", "false"]);

interface CellRendererOptions {
  // Global formatting (applied to all values)
  convertString?: boolean;
  globalPrefix?: string;
  globalSuffix?: string;

  // String formatting
  stringCase?: "upper" | "lower" | "capitalize" | "title";
  stringTruncate?: number;
  stringTruncateEllipsis?: string;

  // Number formatting
  numberDecimals?: number;
  numberFormat?: "standard" | "currency" | "percentage" | "compact";
  numberCurrency?: string;
  numberLocale?: string;

  // Boolean formatting
  booleanTrueText?: string;
  booleanFalseText?: string;

  // Date formatting
  dateFormat?: "date" | "time" | "datetime" | "relative" | "custom";
  dateCustomFormat?: Intl.DateTimeFormatOptions;
  dateLocale?: string;

  // Null/undefined formatting
  nullText?: string;

  // Object formatting
  objectFormat?: "json" | "toString" | "keys" | "custom";
  objectJsonSpacing?: number;
  objectCustomFormatter?: (obj: unknown) => string;
}

export function automaticCellRenderer(value: unknown, options: CellRendererOptions = {}): ReactNode {
  const {
    convertString = true,
    globalPrefix = "",
    globalSuffix = "",
    stringCase,
    stringTruncate,
    stringTruncateEllipsis = "...",
    numberDecimals,
    numberFormat = "standard",
    numberCurrency = "EUR",
    numberLocale,
    booleanTrueText = "Yes",
    booleanFalseText = "No",
    dateFormat = "date",
    dateCustomFormat,
    dateLocale,
    nullText,
    objectFormat = "json",
    objectJsonSpacing = 0,
    objectCustomFormatter,
  } = options;

  // Helper function to apply global prefix/suffix
  const applyGlobalFormatting = (content: string | ReactNode): ReactNode => {
    if (globalPrefix || globalSuffix) {
      return `${globalPrefix}${content}${globalSuffix}`;
    }
    return content;
  };

  if (value === null || value === undefined) {
    return applyGlobalFormatting(nullText ? nullText : <EmptyValue />);
  }

  const valueType = typeof value;

  if (valueType === "boolean") {
    return applyGlobalFormatting(value ? booleanTrueText : booleanFalseText);
  }

  if (valueType === "number") {
    let formattedNumber: string;
    const resolvedNumberLocale = numberLocale ?? getCurrentNumberFormatLocale();

    switch (numberFormat) {
      case "currency":
        formattedNumber = createCurrencyFormatter({
          locale: resolvedNumberLocale,
          currency: numberCurrency,
          fractionDigits: numberDecimals,
        }).format(value as number);
        break;
      case "percentage":
        formattedNumber = new Intl.NumberFormat(resolvedNumberLocale, {
          style: "percent",
          ...(numberDecimals !== undefined && {
            minimumFractionDigits: numberDecimals,
            maximumFractionDigits: numberDecimals,
          }),
        }).format((value as number) / 100);
        break;
      case "compact":
        formattedNumber = new Intl.NumberFormat(resolvedNumberLocale, {
          notation: "compact",
          ...(numberDecimals !== undefined && {
            minimumFractionDigits: numberDecimals,
            maximumFractionDigits: numberDecimals,
          }),
        }).format(value as number);
        break;
      default:
        if (numberDecimals !== undefined) {
          formattedNumber = new Intl.NumberFormat(resolvedNumberLocale, {
            minimumFractionDigits: numberDecimals,
            maximumFractionDigits: numberDecimals,
          }).format(value as number);
        } else {
          formattedNumber = (value as number).toLocaleString(resolvedNumberLocale);
        }
    }

    return applyGlobalFormatting(formattedNumber);
  }

  if (value instanceof Date) {
    return applyGlobalFormatting(formatDate(value, dateFormat, dateCustomFormat, dateLocale));
  }

  if (valueType === "string") {
    const stringValue = value as string;

    if (stringValue.length === 0) {
      return applyGlobalFormatting(nullText ? nullText : <EmptyValue />);
    }

    if (convertString) {
      const lowerValue = stringValue.toLowerCase();
      if (BOOLEAN_STRINGS.has(lowerValue)) {
        const boolValue = lowerValue === "true";
        return applyGlobalFormatting(boolValue ? booleanTrueText : booleanFalseText);
      }

      if (NUMERIC_PATTERN.test(stringValue)) {
        const numValue = Number(stringValue);
        if (!Number.isNaN(numValue)) {
          return automaticCellRenderer(numValue, options);
        }
      }

      if (ISO_DATE_PATTERN.test(stringValue)) {
        const dateValue = new Date(stringValue);
        if (!Number.isNaN(dateValue.getTime())) {
          return applyGlobalFormatting(formatDate(dateValue, dateFormat, dateCustomFormat, dateLocale));
        }
      }
    }

    return applyGlobalFormatting(formatString(stringValue, stringCase, stringTruncate, stringTruncateEllipsis));
  }

  if (valueType === "object") {
    let objectResult: string;

    switch (objectFormat) {
      case "toString":
        objectResult = String(value);
        break;
      case "keys":
        if (value && typeof value === "object") {
          objectResult = Object.keys(value).join(", ");
        } else {
          objectResult = "[Object]";
        }
        break;
      case "custom":
        if (objectCustomFormatter) {
          objectResult = objectCustomFormatter(value);
        } else {
          objectResult = "[Object]";
        }
        break;
      default:
        try {
          objectResult = JSON.stringify(value, null, objectJsonSpacing);
        } catch {
          objectResult = "[Object]";
        }
    }

    return applyGlobalFormatting(objectResult);
  }

  return applyGlobalFormatting(String(value));
}

function formatString(
  str: string,
  stringCase?: "upper" | "lower" | "capitalize" | "title",
  truncate?: number,
  ellipsis = "...",
): string {
  let formatted = str;

  // Apply case formatting
  switch (stringCase) {
    case "upper":
      formatted = formatted.toUpperCase();
      break;
    case "lower":
      formatted = formatted.toLowerCase();
      break;
    case "capitalize":
      formatted = formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
      break;
    case "title":
      formatted = formatted.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
      break;
  }

  // Apply truncation
  if (truncate !== undefined && formatted.length > truncate) {
    formatted = formatted.substring(0, truncate) + ellipsis;
  }

  return formatted;
}

function formatDate(
  date: Date,
  format: "date" | "time" | "datetime" | "relative" | "custom",
  customFormat?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  const { dateFormat } = userConfigStore.getState();

  switch (format) {
    case "time":
      return buildTimeString(date);
    case "datetime":
      return `${buildDateString(date, dateFormat)} ${buildTimeString(date)}`;
    case "relative":
      return formatRelativeTime(date);
    case "custom":
      if (customFormat) {
        return new Intl.DateTimeFormat(locale, customFormat).format(date);
      }
      return buildDateString(date, dateFormat);
    default:
      return buildDateString(date, dateFormat);
  }
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes !== 1 ? "s" : ""} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours !== 1 ? "s" : ""} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays} day${diffInDays !== 1 ? "s" : ""} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths !== 1 ? "s" : ""} ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} year${diffInYears !== 1 ? "s" : ""} ago`;
}
